/**
 * Error taxonomy for the public archive API.
 *
 * Branch on the HTTP status, never on the envelope's `code` field: a validation
 * failure returns HTTP 400 while `code` is 500 (docs/api.md, "Response envelope").
 */
export type ApiErrorKind =
  /** HTTP 429. Back off and tell the user to retry — never retry in a loop. */
  | 'rate_limited'
  /** HTTP 404 "disclosure not found". Covers missing/unpublished/expired/malformed IDs, deliberately indistinguishable. */
  | 'not_found'
  /** HTTP 404 on the creative endpoint: a third party served the ad, so no copy will ever exist. */
  | 'creative_unavailable'
  /** HTTP 400 "page is beyond the maximum" — the 200-page cap. */
  | 'page_cap'
  /**
   * HTTP 400 "the state list is temporarily unavailable" — the state configuration
   * could not be read. Documented as transient, so unlike every other 400 this one
   * is worth retrying.
   */
  | 'state_config_unavailable'
  /** Any other HTTP 400. */
  | 'validation'
  /** HTTP 5xx. */
  | 'server'
  /** Transport failure, CORS, or an unreadable body. */
  | 'network'

/** The creative 404 is worded differently from the record 404 on purpose. */
const NO_ARCHIVED_COPY = 'no archived copy'
const PAGE_BEYOND_MAXIMUM = 'page is beyond the maximum'
const STATE_LIST_UNAVAILABLE = 'state list is temporarily unavailable'

export class ApiError extends Error {
  readonly kind: ApiErrorKind
  /** HTTP status, or 0 when the request never got a response. */
  readonly status: number
  /** The API's own message (`data` on an error response), when there was one. */
  readonly detail: string | null

  constructor(kind: ApiErrorKind, status: number, message: string, detail: string | null = null) {
    super(message)
    this.name = 'ApiError'
    this.kind = kind
    this.status = status
    this.detail = detail
  }
}

/** Classify a failed response by HTTP status, with the API message only used to split the two 404s. */
export function classify(status: number, detail: string | null): ApiError {
  const message = detail?.trim() ?? ''
  const lower = message.toLowerCase()

  if (status === 429) {
    return new ApiError(
      'rate_limited',
      status,
      // The API's own wording is written for a human and safe to show directly.
      message || 'Too many requests. Please slow down and try again shortly.',
      message || null,
    )
  }

  if (status === 404) {
    if (lower.includes(NO_ARCHIVED_COPY)) {
      return new ApiError(
        'creative_unavailable',
        status,
        'No archived copy of this advertisement is available.',
        message,
      )
    }
    return new ApiError('not_found', status, 'Disclosure not found.', message || null)
  }

  if (status === 400) {
    if (lower.includes(STATE_LIST_UNAVAILABLE)) {
      return new ApiError(
        'state_config_unavailable',
        status,
        message || 'The state list is temporarily unavailable.',
        message || null,
      )
    }

    if (lower.includes(PAGE_BEYOND_MAXIMUM)) {
      return new ApiError(
        'page_cap',
        status,
        'That page is beyond the maximum. Narrow your search instead.',
        message,
      )
    }
    return new ApiError('validation', status, message || 'The search request was not valid.', message || null)
  }

  if (status >= 500) {
    return new ApiError('server', status, 'The public archive is temporarily unavailable.', message || null)
  }

  return new ApiError('network', status, 'The public archive could not be reached.', message || null)
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError
}

/**
 * Failures the reader can do nothing about, and that mean the archive is not
 * working as published: a server fault, a transport failure, or a response we
 * could not read.
 *
 * These are the only ones escalated to the full error page. A 404, a 429, a page
 * cap or a validation message are all normal, self-explanatory outcomes that the
 * reader resolves themselves — sending those to a support page would be wrong.
 */
export function isUnexpectedError(error: unknown): boolean {
  if (!isApiError(error)) return true
  return error.kind === 'server' || error.kind === 'network'
}

/**
 * Which failures are worth retrying automatically.
 *
 * 429 is deliberately excluded — retrying is exactly what the rate limit exists to
 * prevent. The transient state-configuration 400 is included despite being a 4xx,
 * because the API documents it as retryable.
 */
export function isRetryable(error: unknown): boolean {
  if (!isApiError(error)) return false
  return (
    error.kind === 'server' ||
    error.kind === 'network' ||
    error.kind === 'state_config_unavailable'
  )
}
